import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect, useMemo, useRef } from "react";

const getLatLngsFromEncodedPaths = (geometryLibrary, paths) => {
  var markers = [];
  paths
    .filter((path) => path != null)
    .forEach((encodedPath) => {
      const path = geometryLibrary.encoding.decodePath(encodedPath);

      path.forEach((point) => {
        markers.push({
          lat: point.lat(),
          lng: point.lng(),
        });
      });
    });
  return markers;
};

const getLatLngsFromJobs = (jobs) => {
  var markers = [];
  jobs
    .filter((job) => job.location?.lat != null)
    .forEach((job) => markers.push(job.location));
  return markers;
};

export function useMapJobsZoom(jobs = []) {
  let map = useMap();
  const geometryLibrary = useMapsLibrary("geometry");

  const resetZoom = useMemo(
    () => () => {
      if (!map || jobs.length === 0 || !geometryLibrary) return;
      // single time effect, we want to zoom in on our jobs to make it easier to see
      var bounds = new google.maps.LatLngBounds();

      var _markers = getLatLngsFromJobs(jobs);
      _markers.forEach((marker) => {
        bounds.extend(marker);
      });

      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, 100);
      }
    },
    [jobs, geometryLibrary, map],
  );
  useEffect(() => {
    resetZoom();
  }, [resetZoom]);
  return resetZoom;
}

/**
 * Marker positions allow fitting before driving paths exist. Keep the omitted
 * value undefined so legacy callers do not get a new array dependency each render.
 * @param {(bounds: google.maps.LatLngBounds) => void} [extendBoundsCallback]
 * @param {google.maps.LatLngLiteral[]} [markerPositions]
 * @param {string} [autoFitKey] When supplied, fit once per key and map instance.
 */
export function useMapPathZoom(
  paths = [],
  resetPaths = [],
  extendBoundsCallback = undefined,
  markerPositions = undefined,
  autoFitKey = undefined,
) {
  let map = useMap();
  const geometryLibrary = useMapsLibrary("geometry");
  const lastFit = useRef(null);

  useEffect(() => {
    if (
      !map ||
      !geometryLibrary ||
      (paths.length === 0 && !markerPositions?.length) ||
      (autoFitKey !== undefined &&
        lastFit.current?.map === map &&
        lastFit.current?.key === autoFitKey)
    )
      return;
    // single time effect, we want to zoom in on our jobs to make it easier to see
    var bounds = new google.maps.LatLngBounds();

    var markers = getLatLngsFromEncodedPaths(geometryLibrary, paths);
    markers.forEach((marker) => {
      bounds.extend(marker);
    });
    markerPositions?.forEach((position) => bounds.extend(position));

    if (extendBoundsCallback) {
      extendBoundsCallback(bounds);
    }

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds);
      // Record only successful fits so delayed map data can still fit initially.
      lastFit.current = { map, key: autoFitKey };
    }

    return () => {
      if (resetPaths.length === 0 || !geometryLibrary) return;
      var bounds = new google.maps.LatLngBounds();

      var markers = getLatLngsFromEncodedPaths(geometryLibrary, resetPaths);
      markers.forEach((marker) => {
        bounds.extend(marker);
      });

      if (!bounds.isEmpty()) {
        map.fitBounds(bounds);
      }
    };
  }, [
    paths,
    resetPaths,
    geometryLibrary,
    map,
    extendBoundsCallback,
    markerPositions,
    autoFitKey,
  ]);
}
